import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  ChamberAccountRoleEnum,
  UserType,
  useDeleteChamberStaffUserMutation,
  useEnableChamberStaffUserMutation,
  useGetChamberStaffUsersQuery,
  useRegenerateChamberStaffCredentialsMutation,
  useSuspendChamberStaffUserMutation,
  useUpdateChamberStaffUserMutation,
} from "../../../../../graphql/types";
import { RoutePaths } from "../../../../../enumStrings";
import { ActionSuccessDialog } from "../../../../common/dashboard/ActionSuccessDialog";
import ActivityLogView from "./ActivityLogView";
import CredentialsDownloadDialog from "./CredentialsDownloadDialog";
import EmployeeAccessConfirmDialog from "./EmployeeAccessConfirmDialog";
import EmployeePermissionsDialog from "./EmployeePermissionsDialog";
import {
  buildStaffCredentialsFile,
  staffCredentialsNeverIssued,
} from "./staffCredentialsFile";
import { normalizePermissionSelection } from "./permissionCatalogue";
import StaffUserForm, { EMPLOYEE_FORM_INTRO_TEXT } from "./StaffUserForm";
import {
  StaffUserAction,
  StaffUsersTable,
} from "./StaffUsersTable";
import {
  ChamberStaffCredentialDeliveryStatus,
  ChamberStaffUserViewModel,
  StaffUserFormValue,
} from "./types";
import {
  downloadTextFile,
  getOperationErrorMessage,
  PRESERVE_ERROR_FOR_CALLER_CONTEXT,
} from "./uiUtilities";
import {
  isManageableChamberStaffUser,
  isPermanentlyDisabledStaffUser,
} from "./staffUserIdentity";
import { buildStaffErrorFallback, STAFF_SUPPORT_EMAIL } from "../../../../../utils/userFacingError";
import { StaffUserCreationFeedback } from "./staffUserCreationFeedback";
import { toChamberStaffUsagePermissionsInput } from "./staffUserMutationInputs";

export type UsersPermissionsAccessMode = "owner" | "activity-only";

export type UsersPermissionsTabProps = {
  accessMode: UsersPermissionsAccessMode;
  initialCreationFeedback?: StaffUserCreationFeedback | null;
  timeZone?: string;
};

type Feedback = {
  severity: "success" | "info" | "warning";
  message: string;
};

// Claude Design Users & Permission list controls (D:6938-6990).
const designGoldButtonClassName =
  "inline-flex shrink-0 items-center justify-center rounded-[8px] border-0 bg-[#E1A731] font-semibold text-white transition hover:bg-[#C6912A] disabled:cursor-not-allowed disabled:opacity-50";

type PendingAction = Exclude<StaffUserAction, "update" | "viewPermissions">;

// Task text for the fallback "We could not [task]." of each row action.
const STAFF_ACTION_TASKS: Record<PendingAction, string> = {
  delete: "delete this employee's access",
  credentials: "create the new login credentials",
  suspend: "suspend this employee's access",
  enable: "unsuspend this employee's access",
};

const toStaffUserViewModel = (staffUser: {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  userType: UserType;
  chamberAccountRole: ChamberAccountRoleEnum;
  jobTitle?: string | null;
  accountStatus: string;
  credentialDeliveryStatus: string;
  credentialsLastSentAtUtcMilli?: number | null;
  credentialsLastDownloadedAtUtcMilli?: number | null;
  lastLoginAtUtcMilli?: number | null;
  createdAtUtcMilli: number;
  updatedAtUtcMilli: number;
  deletedAtUtcMilli?: number | null;
  usagePermissions: {
    chamberMemberPro_EnabledPages?: readonly string[] | null;
    chamberPerksAppAdminConsole_EnabledPages?: readonly string[] | null;
  };
}): ChamberStaffUserViewModel => ({
  id: staffUser.id,
  email: staffUser.email,
  firstName: staffUser.firstName,
  lastName: staffUser.lastName,
  jobTitle: staffUser.jobTitle ?? "",
  accountStatus:
    staffUser.accountStatus === "ACTIVE"
      ? "ACTIVE"
      : staffUser.accountStatus === "DELETION_PENDING"
        ? "DELETION_PENDING"
        : "SUSPENDED",
  credentialDeliveryStatus: (
    ["NOT_SENT", "SUPPRESSED", "SENT", "FAILED", "UNKNOWN"].includes(
      staffUser.credentialDeliveryStatus
    )
      ? staffUser.credentialDeliveryStatus
      : "UNKNOWN"
  ) as ChamberStaffCredentialDeliveryStatus,
  credentialsLastSentAtUtcMilli: staffUser.credentialsLastSentAtUtcMilli,
  credentialsLastDownloadedAtUtcMilli:
    staffUser.credentialsLastDownloadedAtUtcMilli,
  lastLoginAtUtcMilli: staffUser.lastLoginAtUtcMilli,
  createdAtUtcMilli: staffUser.createdAtUtcMilli,
  updatedAtUtcMilli: staffUser.updatedAtUtcMilli,
  deletedAtUtcMilli: staffUser.deletedAtUtcMilli ?? null,
  permissions: normalizePermissionSelection({
    chamberMemberPro: [
      ...(staffUser.usagePermissions.chamberMemberPro_EnabledPages ?? []),
    ],
    chamberPerksAppAdminConsole: [
      ...(staffUser.usagePermissions
        .chamberPerksAppAdminConsole_EnabledPages ?? []),
    ],
  }),
});

const userFullName = (user: ChamberStaffUserViewModel) =>
  `${user.firstName} ${user.lastName}`;

const getPendingActionDialog = ({
  action,
  user,
}: {
  action: Exclude<PendingAction, "credentials">;
  user: ChamberStaffUserViewModel;
}) => {
  const name = userFullName(user);

  switch (action) {
    // CMP Dashboard (24) userConfirmTitle / userConfirmMsg / userConfirmBtn /
    // userConfirmColor: red for suspend and delete, gold for unsuspend.
    case "suspend":
      return {
        title: "Suspend Employee Access",
        message: `Are you sure you want to suspend access for ${name}? They will not be able to log in until access is restored. Their permissions are kept.`,
        confirmLabel: "Suspend Employee Access",
        tone: "red" as const,
      };
    case "enable":
      return {
        title: "Unsuspend Employee Access",
        message: `Unsuspend access for ${name}? They will be able to log in again with their existing permissions.`,
        confirmLabel: "Unsuspend Employee Access",
        tone: "gold" as const,
      };
    case "delete":
      // IF12 S2: the row stays view only.
      return {
        title: "Delete Employee Access",
        message:
          `Are you sure you want to delete ${name}? They will lose all access to the Chamber Member Pro™ Dashboard and Website Builder immediately and show as Permanently Disabled at the bottom of the list. Their record and past activity stay view only in the Employee Activity Log. This cannot be undone.`,
        confirmLabel: "Delete Employee Access",
        tone: "red" as const,
      };
  }
};

export const UsersPermissionsTab = ({
  accessMode,
  initialCreationFeedback,
  timeZone,
}: UsersPermissionsTabProps) => {
  const navigate = useNavigate();
  const isOwner = accessMode === "owner";
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [feedback, setFeedback] = useState<Feedback | null>(() =>
    initialCreationFeedback
      ? {
          severity: initialCreationFeedback.inlineSeverity,
          message: initialCreationFeedback.inlineMessage,
        }
      : null
  );
  const [editingUser, setEditingUser] =
    useState<ChamberStaffUserViewModel | null>(null);
  const [pendingAction, setPendingAction] = useState<{
    action: PendingAction;
    user: ChamberStaffUserViewModel;
  } | null>(null);
  const [actionLoadingUserId, setActionLoadingUserId] = useState<string | null>(
    null
  );
  const [permissionsUser, setPermissionsUser] =
    useState<ChamberStaffUserViewModel | null>(null);

  const {
    data,
    loading: staffUsersLoading,
    error: staffUsersError,
    refetch: refetchStaffUsers,
  } = useGetChamberStaffUsersQuery({
    skip: !isOwner,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
  });
  const [updateStaffUser, { loading: updateLoading }] =
    useUpdateChamberStaffUserMutation({
      context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
    });
  // no-cache: the temporary password never enters the Apollo cache.
  const [regenerateCredentials] = useRegenerateChamberStaffCredentialsMutation({
    context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
    fetchPolicy: "no-cache",
  });
  const [suspendStaffUser] = useSuspendChamberStaffUserMutation({
    context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
  });
  const [enableStaffUser] = useEnableChamberStaffUserMutation({
    context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
  });
  const [deleteStaffUser] = useDeleteChamberStaffUserMutation({
    context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
  });

  const users = useMemo(
    () =>
      (data?.getChamberStaffUsers ?? [])
        .filter(isManageableChamberStaffUser)
        .map((staffUser) => toStaffUserViewModel(staffUser))
        // CMP Dashboard (24): Permanently Disabled rows go last, order kept.
        .sort(
          (left, right) =>
            Number(isPermanentlyDisabledStaffUser(left)) -
            Number(isPermanentlyDisabledStaffUser(right))
        ),
    [data]
  );

  if (showActivityLog) {
    return (
      <ActivityLogView
        onBack={() => setShowActivityLog(false)}
        timeZone={timeZone}
      />
    );
  }

  if (!isOwner) {
    return (
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setShowActivityLog(true)}
          className={`${designGoldButtonClassName} px-[26px] py-[13px] text-[15px]`}
        >
          View Employee Activity Log
        </button>
      </div>
    );
  }

  const refreshAfterCommittedMutation = async (
    completionFeedback: Feedback
  ): Promise<void> => {
    setFeedback(completionFeedback);
    try {
      await refetchStaffUsers();
    } catch (_refreshError) {
      // The action worked, so the pop-up keeps its own tone.
      setFeedback({
        severity: completionFeedback.severity,
        message: `${completionFeedback.message} The employee list could not refresh. Click Retry before you take another action.`,
      });
    }
  };

  const updateUser = async (value: StaffUserFormValue) => {
    if (!editingUser) {
      return;
    }

    setUpdateError("");
    setFeedback(null);

    try {
      const result = await updateStaffUser({
        variables: {
          input: {
            staffUserId: editingUser.id,
            firstName: value.firstName,
            lastName: value.lastName,
            jobTitle: value.jobTitle || null,
            usagePermissions: toChamberStaffUsagePermissionsInput({
              selection: value.permissions,
            }),
          },
        },
      });
      const response = result.data?.updateChamberStaffUser;

      if (
        !response ||
        !isManageableChamberStaffUser(response.staffUser)
      ) {
        throw new Error("The updated staff account was not returned.");
      }

      setEditingUser(null);
      await refreshAfterCommittedMutation({
        severity: "success",
        message:
          response.message ||
          `${value.firstName} ${value.lastName} was updated.`,
      });
    } catch (operationError) {
      setUpdateError(
        getOperationErrorMessage(
          operationError,
          buildStaffErrorFallback("update this employee")
        )
      );
    }
  };

  const executePendingAction = async () => {
    if (!pendingAction) {
      return;
    }

    const { action, user } = pendingAction;
    const name = userFullName(user);
    setActionLoadingUserId(user.id);
    setFeedback(null);

    try {
      let completionFeedback: Feedback;
      if (action === "delete") {
        const result = await deleteStaffUser({
          variables: { staffUserId: user.id },
        });

        if (!result.data?.deleteChamberStaffUser.deleted) {
          throw new Error("The staff account was not deleted.");
        }

        // CMP Dashboard (24) userNotice after a delete.
        completionFeedback = {
          severity: "success",
          message: `Access for ${name} has been deleted. They now show as Permanently Disabled with view-only access to their record.`,
        };
      } else if (action === "credentials") {
        const neverIssued = staffCredentialsNeverIssued(user);
        const response = (
          await regenerateCredentials({
            variables: { staffUserId: user.id },
          })
        ).data?.regenerateChamberStaffCredentials;

        if (!response?.temporaryPassword) {
          throw new Error("The new login credentials were not returned.");
        }

        downloadTextFile({
          ...buildStaffCredentialsFile({
            name,
            jobTitle: user.jobTitle,
            email: user.email,
            temporaryPassword: response.temporaryPassword,
            generatedAtUtcMilli: response.generatedAtUtcMilli,
            timeZone,
          }),
          type: "text/plain",
        });
        // CMP Dashboard (24) userNotice after downloadCreds.
        completionFeedback = {
          severity: "success",
          message: neverIssued
            ? `Login credentials for ${name} have been generated and downloaded.`
            : `New login credentials for ${name} have been generated and downloaded. Their previous password no longer works.`,
        };
      } else {
        const response =
          action === "suspend"
            ? (
                await suspendStaffUser({
                  variables: { staffUserId: user.id },
                })
              ).data?.suspendChamberStaffUser
            : (
                await enableStaffUser({
                  variables: { staffUserId: user.id },
                })
              ).data?.enableChamberStaffUser;

        if (
          !response ||
          !isManageableChamberStaffUser(response.staffUser)
        ) {
          throw new Error("The staff account action did not return a result.");
        }

        completionFeedback = {
          severity: "success",
          // CMP Dashboard (24) userNotice: "Access for <name> has been
          // suspended." / "... has been unsuspended." (was "enabled").
          message:
            response.message ||
            `Access for ${name} has been ${
              action === "suspend" ? "suspended" : "unsuspended"
            }.`,
        };
      }

      setPendingAction(null);
      await refreshAfterCommittedMutation(completionFeedback);
    } catch (operationError) {
      setFeedback({
        severity: "warning",
        message: getOperationErrorMessage(
          operationError,
          buildStaffErrorFallback(STAFF_ACTION_TASKS[action])
        ),
      });
    } finally {
      setActionLoadingUserId(null);
    }
  };

  const pendingDialog =
    pendingAction && pendingAction.action !== "credentials"
      ? getPendingActionDialog({
          action: pendingAction.action,
          user: pendingAction.user,
        })
      : null;

  return (
    <div className="flex flex-col gap-[24px]">
      <ActionSuccessDialog
        message={feedback?.message}
        tone={feedback?.severity === "warning" ? "error" : "success"}
        onClose={() => setFeedback(null)}
      />

      <div className="flex flex-col items-start justify-between gap-[20px] sm:flex-row">
        <div>
          <h2
            id="staff-users-heading"
            className="text-[18px] font-semibold text-[#0A0A0A]"
          >
            Employees &amp; Permissions
          </h2>
          <p className="mt-[4px] text-[14px] text-[#0A0A0A]">
            Manage existing employees, generate or re-generate login
            credentials, update permissions, or suspend or delete access.
          </p>
        </div>
        {/* The CTO kept the dedicated create-user page; this button opens it. */}
        <button
          type="button"
          onClick={() =>
            navigate(RoutePaths.chamberAdmin_myChamber_createStaffUser)
          }
          className={`${designGoldButtonClassName} px-[20px] py-[12px] text-[14px]`}
        >
          {"+\u00A0 Add New Employee"}
        </button>
      </div>

      <section
        aria-labelledby="staff-users-heading"
        className="flex flex-col gap-[22px] rounded-[12px] border border-[#ECECEC] bg-white p-7 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
      >
        <StaffUsersTable
          users={users}
          loading={staffUsersLoading}
          error={
            staffUsersError
              ? `We could not load the employees. Click Retry. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`
              : undefined
          }
          actionLoadingUserId={actionLoadingUserId}
          onRetry={() => refetchStaffUsers()}
          onAction={(action, user) => {
            if (action === "update") {
              setUpdateError("");
              setEditingUser(user);
            } else if (action === "viewPermissions") {
              setPermissionsUser(user);
            } else {
              setPendingAction({ action, user });
            }
          }}
        />
      </section>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setShowActivityLog(true)}
          className={`${designGoldButtonClassName} px-[26px] py-[13px] text-[15px]`}
        >
          View Employee Activity Log
        </button>
      </div>

      <Dialog
        open={Boolean(editingUser)}
        onClose={(_, reason) => {
          if (reason === "backdropClick") {
            return;
          }
          if (!updateLoading) {
            setEditingUser(null);
            setUpdateError("");
          }
        }}
        disableEscapeKeyDown={updateLoading}
        maxWidth={false}
        fullWidth
        aria-labelledby="update-staff-user-dialog-title"
        aria-describedby="update-staff-user-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "calc(100vh - 32px)",
            maxWidth: "1152px",
            width: "calc(100% - 32px)",
          },
        }}
      >
        <DialogTitle
          id="update-staff-user-dialog-title"
          sx={{ fontSize: 22, fontWeight: 900, pb: 0.5, pr: 7 }}
        >
          Update Employee Permissions
          <IconButton
            aria-label="Close update employee dialog"
            disabled={updateLoading}
            onClick={() => {
              setEditingUser(null);
              setUpdateError("");
            }}
            sx={{ position: "absolute", right: 16, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 3, pt: "8px !important" }}>
          <p
            id="update-staff-user-dialog-description"
            className="mb-6 text-sm leading-6 text-gray-700"
          >
            {EMPLOYEE_FORM_INTRO_TEXT}
          </p>
          {editingUser ? (
            <StaffUserForm
              key={`${editingUser.id}-${editingUser.updatedAtUtcMilli}`}
              mode="update"
              initialValue={editingUser}
              onSubmit={updateUser}
              onCancel={() => {
                if (!updateLoading) {
                  setEditingUser(null);
                  setUpdateError("");
                }
              }}
              loading={updateLoading}
              submitError={updateError}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <EmployeePermissionsDialog
        user={permissionsUser}
        onClose={() => setPermissionsUser(null)}
      />

      <CredentialsDownloadDialog
        open={pendingAction?.action === "credentials"}
        neverIssued={
          pendingAction ? staffCredentialsNeverIssued(pendingAction.user) : false
        }
        name={pendingAction ? userFullName(pendingAction.user) : ""}
        email={pendingAction?.user.email ?? ""}
        busy={Boolean(actionLoadingUserId)}
        onDownload={executePendingAction}
        onClose={() => {
          if (!actionLoadingUserId) {
            setPendingAction(null);
          }
        }}
      />

      <EmployeeAccessConfirmDialog
        open={Boolean(pendingAction && pendingDialog)}
        title={pendingDialog?.title ?? ""}
        message={pendingDialog?.message ?? ""}
        confirmLabel={pendingDialog?.confirmLabel ?? ""}
        tone={pendingDialog?.tone ?? "red"}
        loading={Boolean(actionLoadingUserId)}
        onConfirm={executePendingAction}
        onClose={() => {
          if (!actionLoadingUserId) {
            setPendingAction(null);
          }
        }}
      />
    </div>
  );
};

export default UsersPermissionsTab;
